import "./../JSONTools/JSONformatter.css";
import ToolHeader from "../../Components/ToolHeader";
import { Row, Col } from "react-bootstrap";
import { useState, useRef, useCallback, useMemo} from "react";
import {  DocxtoMarkDownAPI } from "../../Services/APIService";

// Name of the field in the JSON response that holds the Markdown string.
// If your backend just returns plain text (no JSON wrapper), this is ignored automatically.
const RESPONSE_FIELD = "markdown";

function formatBytes(bytes) {
    if (!bytes) return "0 KB";
    const kb = bytes / 1024;
    if (kb < 1024) return `${kb.toFixed(0)} KB`;
    return `${(kb / 1024).toFixed(2)} MB`;
}

// Minimal markdown -> JSX renderer covering headings, bold, and paragraphs.
// Swap this out for a real markdown library in your actual project if you have one available.
function renderMarkdown(markdown) {
    if (!markdown) return null;
    const blocks = markdown.split(/\n{2,}/);

    const renderInline = (text) => {
        const parts = text.split(/(\*\*[^*]+\*\*)/g);
        return parts.map((part, i) => {
            if (part.startsWith("**") && part.endsWith("**")) {
                return <strong key={i}>{part.slice(2, -2)}</strong>;
            }
            return <span key={i}>{part}</span>;
        });
    };

    return blocks.map((block, i) => {
        const trimmed = block.trim();
        const headingMatch = trimmed.match(/^(#{1,6})\s+(.*)$/);
        if (headingMatch) {
            const level = headingMatch[1].length;
            const Tag = `h${Math.min(level, 6)}`;
            return (
                <Tag key={i} className="dmc-md-heading">
                    {renderInline(headingMatch[2])}
                </Tag>
            );
        }
        const boldOnlyMatch = trimmed.match(/^\*\*([^*]+)\*\*$/);
        if (boldOnlyMatch) {
            return (
                <p key={i} className="dmc-md-bold-line">
                    {boldOnlyMatch[1]}
                </p>
            );
        }
        return (
            <p key={i} className="dmc-md-paragraph">
                {renderInline(trimmed)}
            </p>
        );
    });
}


function DocxtoMarkdown() {
    const toolFav = { toolName: 'DOCX to Markdown', Iconpath: "/FileCodeRaw.svg" };
    const [file, setFile] = useState(null);
    const [dragActive, setDragActive] = useState(false);
    const [stage, setStage] = useState("idle"); // idle | converting | done | error
    const [markdown, setMarkdown] = useState("");
    const [activeTab, setActiveTab] = useState("raw"); // raw | preview
    const [errorMsg, setErrorMsg] = useState("");
    const inputRef = useRef(null);


    

    

    const handleFiles = useCallback((fileList) => {
        const picked = fileList?.[0];
        if (!picked) return;
        const okType = /\.docx?$/i.test(picked.name);
        if (!okType) {
            setErrorMsg("Please upload a .docx or .doc file.");
            return;
        }
        setErrorMsg("");
        setFile(picked);
        setStage("idle");
        setMarkdown("");
    }, []);

    const onDrop = useCallback(
        (e) => {
            e.preventDefault();
            setDragActive(false);
            handleFiles(e.dataTransfer.files);
        },
        [handleFiles]
    );

    const resetAll = () => {
        setFile(null);
        setMarkdown("");
        setStage("idle");
        setErrorMsg("");
        setActiveTab("raw");
        if (inputRef.current) inputRef.current.value = "";
    };

    const handleConvert = async () => {
        if (!file) return;
        setStage("converting");
        setErrorMsg("");

        try {
            const formData = new FormData();
            formData.append("files", file);

            const response = await DocxtoMarkDownAPI(formData);

            // Backend may return plain Markdown text directly, JSON wrapping it under
            // RESPONSE_FIELD, or a Blob if the server sends a file-like response.
            const rawText =
                typeof response.data === "string"
                    ? response.data
                    : response.data instanceof Blob
                    ? await response.data.text()
                    : response.data && typeof response.data === "object"
                    ? JSON.stringify(response.data)
                    : String(response.data);

            let markdownText = rawText;
            try {
                const json = JSON.parse(rawText);
                if (json && typeof json === "object") {
                    markdownText = json[RESPONSE_FIELD] ?? rawText;
                }
            } catch {
                // Not JSON — rawText is already the plain Markdown string.
            }

            if (!markdownText) {
                throw new Error("Response did not contain any Markdown content.");
            }

            setMarkdown(markdownText);
            setActiveTab("raw");
            setStage("done");
        } catch (err) {
            setErrorMsg(err.message || "Something went wrong during conversion.");
            setStage("error");
        }
    };

    const copyMarkdown = async () => {
        if (!markdown) return;
        try {
            await navigator.clipboard.writeText(markdown);
        } catch {
            /* clipboard not available in this context */
        }
    };

    const downloadMarkdown = () => {
        if (!markdown) return;
        const blob = new Blob([markdown], { type: "text/markdown" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = (file?.name || "output").replace(/\.docx?$/i, ".md");
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
    };

    const previewNodes = useMemo(() => renderMarkdown(markdown), [markdown]);
    const hasOutput = stage === "done" && markdown;

    return (
        <>
            <Col>
                <ToolHeader title="DOCX to Markdown" description="Convert DOCX files to Markdown format" toolFav={toolFav} />

                <Row >
                    <div className="dmc-shell">
                        {/* Dropzone */}
                        <div className="dmc-card">
                            <div
                                onDragOver={(e) => {
                                    e.preventDefault();
                                    setDragActive(true);
                                }}
                                onDragLeave={() => setDragActive(false)}
                                onDrop={onDrop}
                                className={`dmc-dropzone ${dragActive ? "dmc-dropzone-active" : ""}`}
                            >
                                <svg
                                    className="dmc-upload-icon"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="1.8"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                >
                                    <path d="M12 16V4M12 4L7 9M12 4L17 9" />
                                    <path d="M4 16v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2" />
                                </svg>
                                <p className="dmc-dropzone-title">Drop your DOCX file here</p>
                                <p className="dmc-dropzone-subtitle">Supported: .docx &middot; Max 25 MB</p>
                                <button type="button" onClick={() => inputRef.current?.click()} className="dmc-btn dmc-btn-primary">
                                    Select file
                                </button>
                                <input
                                    ref={inputRef}
                                    type="file"
                                    accept=".docx,.doc"
                                    className="dmc-hidden-input"
                                    onChange={(e) => handleFiles(e.target.files)}
                                />
                            </div>
                        </div>

                        {/* Convert action row */}
                        <div className="dmc-action-row">
                            <button
                                type="button"
                                disabled={!file || stage === "converting"}
                                onClick={handleConvert}
                                className="dmc-btn dmc-btn-primary dmc-btn-grow"
                            >
                                {stage === "converting" ? "Converting..." : "Convert into Markdown"}
                            </button>
                            <button type="button" onClick={resetAll} className="dmc-btn dmc-btn-outline">
                                <svg className="dmc-btn-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
                                    <path d="M5 7h14M9 7V5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2m2 0-1 13a1 1 0 0 1-1 1H8a1 1 0 0 1-1-1L6 7" />
                                </svg>
                                Clear
                            </button>
                            {/* <button type="button" onClick={resetAll} className="dmc-btn dmc-btn-outline">
                                <svg className="dmc-btn-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
                                    <path d="M12 5v14M5 12h14" />
                                </svg>
                                New
                            </button> */}
                        </div>

                        {/* File chip */}
                        {file && (
                            <div className="dmc-file-chip">
                                <svg className="dmc-file-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
                                    <path d="M7 3h7l4 4v13a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1Z" />
                                    <path d="M14 3v4h4" />
                                </svg>
                                <div className="dmc-file-meta">
                                    <p className="dmc-file-name">{file.name}</p>
                                    <p className="dmc-file-sub">{formatBytes(file.size)} &middot; Ready</p>
                                </div>
                                <button type="button" onClick={() => { setFile(null); setStage("idle"); setMarkdown(""); }} className="dmc-remove-btn">
                                    &times;
                                </button>
                            </div>
                        )}
                        {errorMsg && <p className="dmc-error-text">{errorMsg}</p>}

                        {/* Output panel */}
                        <div className="dmc-card dmc-output-card">
                            <div className="dmc-output-header">
                                <div className="dmc-tabs">
                                    <button
                                        type="button"
                                        onClick={() => setActiveTab("raw")}
                                        className={`dmc-tab ${activeTab === "raw" ? "dmc-tab-active" : ""}`}
                                    >
                                        <svg className="dmc-tab-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                                            <path d="M8 5 3 12l5 7M16 5l5 7-5 7" />
                                        </svg>
                                        Raw
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setActiveTab("preview")}
                                        className={`dmc-tab ${activeTab === "preview" ? "dmc-tab-active" : ""}`}
                                    >
                                        <svg className="dmc-tab-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                                            <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z" />
                                            <circle cx="12" cy="12" r="3" />
                                        </svg>
                                        Preview
                                    </button>
                                </div>
                                <div className="dmc-output-actions">
                                    <button type="button" disabled={!hasOutput} onClick={copyMarkdown} className="dmc-link-btn">
                                        <svg className="dmc-tab-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
                                            <rect x="9" y="9" width="11" height="11" rx="1.5" />
                                            <path d="M5 15V5a1 1 0 0 1 1-1h10" />
                                        </svg>
                                        Copy
                                    </button>
                                    <button type="button" disabled={!hasOutput} onClick={downloadMarkdown} className="dmc-link-btn">
                                        <svg className="dmc-tab-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
                                            <path d="M12 4v11m0 0-4-4m4 4 4-4" />
                                            <path d="M4 18v1a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-1" />
                                        </svg>
                                        Download
                                    </button>
                                </div>
                            </div>

                            <div className="dmc-output-body">
                                {!hasOutput && (
                                    <p className="dmc-placeholder">Upload a DOCX file and click Convert to see Markdown output here.</p>
                                )}
                                {hasOutput && activeTab === "raw" && <pre className="dmc-raw-text">{markdown}</pre>}
                                {hasOutput && activeTab === "preview" && <div className="dmc-preview-body">{previewNodes}</div>}
                            </div>
                        </div>
                    </div>
                </Row>
            </Col >
        </>
    )
}

export default DocxtoMarkdown;
